import { storeToRefs } from 'pinia';
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { useUserStore } from '@/entities/user/store';
import { useConfirmation } from '@/shared/composables/useConfirmation';
import { toKey } from '@/shared/utils/toKey';
import { SMS_CODE_TYPE_FEEDBACK } from '@/shared/constants/smsCodeConstants';
import {
  FEEDBACK_SUCCESS_TITLE,
  FEEDBACK_SUCCESS_TEXT,
  getFeedbackErrorMessage,
} from '@/entities/feedback/lib/feedbackConstants';
import { THANKS_MODAL_AUTO_CLOSE_DELAY } from '@/entities/appointment/lib/appointmentConstants';
import type { ILawyerCardInfo } from '@/entities/lawyer/model/types/lawyer';
import type { ISendFeedbackData } from '@/entities/feedback/model/types/feedback';
import type { ModalProps } from '@/shared/ui/AppModal/model/types/appModal';
import { logError, normalizeError } from '@/shared/lib/errors';

export type FeedbackDraft = Omit<ISendFeedbackData, 'code'>;

export const useFeedbackFlow = () => {
  const modalStore = useModalStore();
  const userStore = useUserStore();
  const { isAuth } = storeToRefs(userStore);
  const { setConfig } = useConfirmation();
  const { $feedbackService, $tokenService, $authService } = useNuxtApp();

  const openFeedback = (props: ModalProps): void => {
    modalStore.setModalState('feedback', true, props);
  };

  const requestCode = async (phone: string): Promise<void> => {
    const { data } = await $tokenService.getBKeyToken<{ key: string }>();
    const bKeyToken = toKey(data.key);
    await $authService.getAuthCode(
      { login: phone, type: SMS_CODE_TYPE_FEEDBACK },
      bKeyToken,
    );
  };

  const openSuccess = (): void => {
    modalStore.setModalState('feedback', false);
    modalStore.setModalState('confirmation', false);
    modalStore.openSuccess({
      title: FEEDBACK_SUCCESS_TITLE,
      text: FEEDBACK_SUCCESS_TEXT,
      isAutoClose: true,
      closeDelay: THANKS_MODAL_AUTO_CLOSE_DELAY,
    });
  };

  const sendFeedback = async (
    draft: FeedbackDraft,
    options: { code?: string } = {},
  ): Promise<string> => {
    const wasGuest = !isAuth.value;
    const data: ISendFeedbackData = { ...draft, code: options.code ?? '' };

    try {
      const { data: res } = await $feedbackService.sendFeedback(data);
      if (wasGuest && res?.token) {
        await userStore.startSession(res.token);
      }
      openSuccess();
      return '';
    } catch (error) {
      const appError = normalizeError(error);
      logError(appError, { action: 'feedback:send' });
      return appError.kind === 'network'
        ? appError.message
        : getFeedbackErrorMessage(appError.status);
    }
  };

  const openConfirmation = async (
    lawyer: ILawyerCardInfo | null,
    phone: string,
    draft: FeedbackDraft,
  ): Promise<string> => {
    try {
      await requestCode(phone);
    } catch (error) {
      const appError = normalizeError(error);
      logError(appError, { action: 'feedback:request-code' });
      return appError.kind === 'network'
        ? appError.message
        : getFeedbackErrorMessage(appError.status);
    }
    setConfig({
      phone,
      lawyerInfo: lawyer,
      title: 'Проверка телефона. Введите код из SMS',
      label: 'Введите код из SMS для подтверждения',
      submitText: 'Оставить отзыв',
      onSubmit: (code: string) => sendFeedback(draft, { code }),
      onResend: () => requestCode(phone),
    });
    modalStore.setModalState('feedback', false);
    modalStore.setModalState('confirmation', true);
    return '';
  };

  return {
    isAuth,
    openFeedback,
    requestCode,
    sendFeedback,
    openConfirmation,
    openSuccess,
  };
};

export default useFeedbackFlow;
